import {
  Show,
  ListButton,
  EditButton,
  DeleteButton,
  RefreshButton,
  useModal,
} from "@refinedev/antd";
import { Button, Space, Tag, Typography } from "antd";
import { IResourceComponentsProps, useNavigation, useShow } from "@refinedev/core";
import { AppstoreOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { IMetadataOther } from "interfaces";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";
import {
  OTHER_SERVICES_BY_PRODUCT,
  OTHER_PRODUCT_COLORS,
  WIFI_DONGLE_VISIBLE,
  readOtherField,
  fmtAvcsFlatFee,
  isPayPerUseType,
  isFlatFeeType,
} from "components/metadata/otherServiceCatalog";
import { formatEnrollmentDate } from "components/metadata/enrollmentDate";

export const MetadataOtherShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IMetadataOther>();
  const record = result;

  const { list } = useNavigation();

  const { Text } = Typography;

  const {
    modalProps: modalPropsSubscription,
    show: showSubscription,
    close: closeSubscription,
  } = useModal();

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Show Other Services",
        subTitle: "other services configuration",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <ListButton resource="metadata-other">Other Services</ListButton>
            <EditButton resource="metadata-other" recordItemId={record?.id} />
            <DeleteButton
              resource="metadata-other"
              recordItemId={record?.id}
              onSuccess={() => list("metadata-other")}
            />
            <RefreshButton resource="metadata-other" recordItemId={record?.id} />
          </Space>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Button type="default" onClick={showSubscription}>
          Associated Subscription
        </Button>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <ShowSection
          title="Subscription"
          subtitle="Linked subscription record"
          icon={<AppstoreOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField
              label="UKHO Licence"
              value={record?.subscription?.licence?.ukhoLicenceId}
              type="code"
              copyable
            />
            <ShowField
              label="Subscription Name"
              value={record?.subscription?.subscriptionName}
            />
          </ShowFieldGrid>
        </ShowSection>

        {OTHER_SERVICES_BY_PRODUCT.map((group) => (
          <ShowSection
            key={group.product}
            title={group.product}
            subtitle={`${group.services.length} service${group.services.length > 1 ? "s" : ""}`}
            icon={group.icon}
            variant="elevated"
          >
            <Space
              direction="vertical"
              size={16}
              style={{ width: "100%" }}
            >
              {group.services.map((svc) => {
                const status = readOtherField<string>(record, `${svc.prefix}Status`);
                const enrollmentDate = readOtherField<string>(record, `${svc.prefix}EnrollmentDate`);
                const subscriptionType = readOtherField<string>(record, `${svc.prefix}SubscriptionType`);
                const currency = readOtherField<string>(record, `${svc.prefix}Currency`);
                const annualFee = readOtherField<number>(record, `${svc.prefix}AnnualFlatFeePerVessel`);
                const productColor = OTHER_PRODUCT_COLORS[svc.product];
                const isPpu = isPayPerUseType(subscriptionType);
                const isFlatFee = isFlatFeeType(subscriptionType);
                const avcsHelper = fmtAvcsFlatFee(
                  record?.avcsFlatFeeAmount,
                  record?.avcsFlatFeeCurrency,
                );

                return (
                  <ShowSection
                    key={svc.prefix}
                    title={svc.service}
                    subtitle={svc.subtitle}
                    icon={<SettingOutlined />}
                    variant="subtle"
                  >
                    <ShowFieldGrid columns={2}>
                      <ShowField label="Status" value={status} type="status" />
                      <ShowField
                        label="Enrollment Date"
                        value={formatEnrollmentDate(enrollmentDate)}
                      />
                      <ShowField
                        label="Subscription Type"
                        value={subscriptionType}
                      />
                      <ShowField
                        label="Currency"
                        value={currency}
                        type="tag"
                        tagColor={productColor}
                      />
                      {isPpu && svc.usesPayPerUseAmount && (
                        <ShowField
                          label="Pay Per Use Amount"
                          value={readOtherField<number>(record, `${svc.prefix}PayPerUseAmount`)}
                          type="currency"
                          currency={currency ?? undefined}
                        />
                      )}
                      {isPpu && !svc.usesPayPerUseAmount && (
                        <ShowField
                          label="Discount Percentage"
                          value={readOtherField<number>(record, `${svc.prefix}DiscountPercentage`) ?? undefined}
                          type="percentage"
                        />
                      )}
                      <ShowField
                        label="Annual Flat Fee Per Vessel"
                        value={annualFee}
                        type="currency"
                        currency={currency ?? undefined}
                      />
                      {svc.hasWifiDongle && WIFI_DONGLE_VISIBLE && (
                        <>
                          <ShowField
                            label="Wi-Fi Dongle One-Time Cost"
                            value={readOtherField<number>(record, `${svc.prefix}WifiDongleOneTimeCost`)}
                            type="currency"
                            currency={readOtherField<string>(record, `${svc.prefix}WifiDongleCurrency`) ?? undefined}
                          />
                          <ShowField
                            label="Wi-Fi Dongle Currency"
                            value={readOtherField<string>(record, `${svc.prefix}WifiDongleCurrency`)}
                            type="tag"
                            tagColor={productColor}
                          />
                        </>
                      )}
                    </ShowFieldGrid>
                    {isFlatFee && svc.usesPayPerUseAmount && (
                      <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                        {avcsHelper}
                      </Text>
                    )}
                  </ShowSection>
                );
              })}
            </Space>
          </ShowSection>
        ))}
      </Space>

      <ShowModal
        title="Associated Subscription"
        subtitle={record?.subscription?.subscriptionName}
        icon={<AppstoreOutlined />}
        open={modalPropsSubscription.open ?? false}
        onClose={closeSubscription}
        empty={!record?.subscription}
        emptyText="No subscription associated"
        width={700}
      >
        <ShowFieldGrid columns={2}>
          <ShowField
            label="UKHO Licence"
            value={record?.subscription?.licence?.ukhoLicenceId}
            type="code"
            copyable
          />
          <ShowField
            label="Subscription Name"
            value={record?.subscription?.subscriptionName}
          />
          <ShowField
            label="Service Type"
            value={record?.subscription?.serviceType}
            type="tag"
            tagColor="purple"
          />
          <ShowField
            label="Enrollment Date"
            value={
              record?.subscription?.enrollmentDate
                ? dayjs(record.subscription.enrollmentDate).format("DD-MMM-YYYY")
                : undefined
            }
          />
          <ShowField
            label="Licence Type"
            value={record?.subscription?.licenceType}
            type="status"
          />
          <ShowField
            label="Activation Status"
            value={record?.subscription?.activationStatus}
            type="status"
          />
          <ShowField
            label="Hold Status"
            value={record?.subscription?.holdStatus}
            type="status"
          />
          <ShowField
            label="Order Value Threshold"
            value={record?.subscription?.orderValueThreshold}
            type="currency"
          />
          <ShowField
            label="Automated Ordering"
            value={record?.subscription?.automatedOrdering}
            type="status"
          />
          <ShowField
            label="Automated Permit Issuance"
            value={record?.subscription?.automatedPermitIssuance}
            type="status"
          />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
